import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ViewModule,
  ViewList,
  CloudUpload,
  Home,
  AccountCircle,
  Logout,
  Security,
  Settings as SettingsIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

export default function Header({
  sidebarOpen,
  onSidebarToggle,
  folderPath,
  onFolderSelect,
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onUploadClick,
  onSettingsOpen,
  onAdminSettingsOpen,
  onDashboardOpen,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [localSearch, setLocalSearch] = useState(search || '');
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  useEffect(() => {
    setLocalSearch(search || '');
  }, [search]);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
        zIndex: (t) => t.zIndex.drawer - 1,
      }}
    >
      <Toolbar
        sx={{
          gap: { xs: 0.5, sm: 1 },
          py: { xs: 0.5, sm: 1 },
          px: { xs: 1, sm: 2 },
          minHeight: { xs: 48, sm: 64 },
        }}
      >
        <IconButton edge="start" onClick={onSidebarToggle} size={isMobile ? 'small' : 'medium'}>
          <MenuIcon />
        </IconButton>

        {/* Logo - nascosto su mobile */}
        {!isTablet && (
          <Box
            component="img"
            src="/logo-minerva.png"
            alt="Minerva Medica"
            sx={{ height: 28, mr: 2 }}
          />
        )}

        {/* Breadcrumbs */}
        <Breadcrumbs
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
            '& .MuiBreadcrumbs-li': { minWidth: 0 },
          }}
          maxItems={isMobile ? 2 : 5}
          itemsBeforeCollapse={1}
          itemsAfterCollapse={1}
        >
          <Link
            component="button"
            variant="body2"
            underline="hover"
            color={folderPath.length === 0 ? 'text.primary' : 'inherit'}
            onClick={() => onFolderSelect(null)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: folderPath.length === 0 ? 600 : 400, whiteSpace: 'nowrap' }}
          >
            <Home fontSize="small" />
            {!isMobile && 'Root'}
          </Link>
          {folderPath.map((folder, i) => (
            <Link
              key={folder.id}
              component="button"
              variant="body2"
              underline="hover"
              color={i === folderPath.length - 1 ? 'text.primary' : 'inherit'}
              onClick={() => onFolderSelect(folder.id)}
              sx={{
                fontWeight: i === folderPath.length - 1 ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? 80 : 150,
              }}
            >
              {folder.name}
            </Link>
          ))}
        </Breadcrumbs>

        {/* Search */}
        <TextField
          size="small"
          placeholder={isMobile ? 'Cerca...' : 'Cerca file...'}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          sx={{
            width: { xs: 100, sm: 180, md: 240 },
            '& .MuiInputBase-root': { height: { xs: 32, sm: 40 } },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: { xs: 16, sm: 20 } }} color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* View toggle - nascosto su mobile */}
        {!isTablet && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && onViewModeChange(v)}
            size="small"
          >
            <ToggleButton value="grid">
              <ViewModule fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table">
              <ViewList fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Upload button - icona sola su mobile */}
        {isMobile ? (
          <Tooltip title="Carica file">
            <IconButton color="primary" onClick={onUploadClick} size="small">
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={onUploadClick}
            size="small"
          >
            Carica file
          </Button>
        )}

        {/* User menu */}
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
          <Avatar
            sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              bgcolor: 'primary.main',
              fontSize: { xs: 12, sm: 14 },
            }}
          >
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { minWidth: 200, mt: 1 } }}
        >
          <MenuItem disabled>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" fontWeight={600}>
                {user?.username}
              </Typography>
              {user?.email && (
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              )}
            </ListItemText>
          </MenuItem>
          <Divider />
          {/* View toggle nel menu su tablet/mobile */}
          {isTablet && (
            <MenuItem onClick={() => { setAnchorEl(null); onViewModeChange(viewMode === 'grid' ? 'table' : 'grid'); }}>
              <ListItemIcon>
                {viewMode === 'grid' ? <ViewList fontSize="small" /> : <ViewModule fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{viewMode === 'grid' ? 'Vista tabella' : 'Vista griglia'}</ListItemText>
            </MenuItem>
          )}
          {user?.is_staff && (
            <MenuItem onClick={() => { setAnchorEl(null); onDashboardOpen?.(); }}>
              <ListItemIcon>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Dashboard</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => { setAnchorEl(null); onSettingsOpen?.(); }}>
            <ListItemIcon>
              <Security fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sicurezza 2FA</ListItemText>
          </MenuItem>
          {user?.is_staff && (
            <MenuItem onClick={() => { setAnchorEl(null); onAdminSettingsOpen?.(); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Impostazioni</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Esci</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
